import React, {useCallback, useContext, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {TFunction} from 'i18next';
import {animated, useSpring} from '@react-spring/web';
import {ITextStyle, TextStyle, Texture} from 'pixi.js';
import {Container, Sprite, Stage, Text} from '@pixi/react';
import Card, {CardSize} from 'components/Card';
import FullScreenView from 'components/FullScreenView';
import {AppContext} from 'AppContext';
import configuration from 'configuration';

const TableScreen = ({path, items}: {path: string; items: number}) => {
  const {t} = useTranslation();

  const {screenSize: stageSize} = useContext(AppContext);
  const answerCardScale = useMemo(() => getAnswerCardScale(stageSize), [stageSize]);
  const [motion, anim] = useSpring(() => ({from: {y: 2 * stageSize[1]}}));

  const image = useMemo(() => configuration.assetsPath('/') + 'card_screen.jpg', []);
  const imagePosition = useMemo(() => {
    return t(configuration.path2langKey(path, '$image.position'), {
      fallbackLng: 'de',
      defaultValue: undefined,
    });
  }, [path, t]);

  const cardImages = useMemo(() => getImages(t, path, 'cards', items), [items, path, t]);
  const answerImages = useMemo(() => getImages(t, path, 'answers', items), [items, path, t]);

  const texts: Array<{question?: string; answer?: string}> = useMemo(() => {
    const cfg: string[] = t(configuration.path2langKey(path, 'texts'), {
      returnObjects: true,
      defaultValue: [],
    });
    return cfg.map(s => {
      const parts = s.replaceAll('\\n', '\n').split('--', 2);
      const question = parts[0].trimEnd();
      return {question, answer: parts[1]?.trimStart()};
    });
  }, [path, t]);

  const hasAnswers = useMemo(() => {
    for (const item of answerImages) if (item !== undefined) return true;
    for (const item of texts) if (item.answer) return true;
    return false;
  }, [answerImages, texts]);

  const getAnchors = useCallback(
    (e: 'cards' | 'answers') => {
      const def = getDefault(t, path, '$' + e + '.textStyle.default.anchor', [0.5, 0.5], false);
      const result: Array<[number, number]> = [];
      for (let i = 1; i <= items; i++) {
        result.push(
          t(configuration.path2langKey(path, '$' + e + '.textStyle.' + String(i) + '.anchor'), {
            returnObjects: true,
            defaultValue: def,
            fallbackLng: 'de',
          }),
        );
      }
      return result;
    },
    [items, path, t],
  );

  const getStyles = useCallback(
    (e: 'cards' | 'answers') => {
      const dts = e === 'cards' ? DefaultCardTextStyle : DefaultAnswerTextStyle;
      const def = getDefault(t, path, '$' + e + '.textStyle.default', dts, true);
      const result: TextStyle[] = [];
      for (let i = 1; i <= items; i++) {
        const cfg = t(configuration.path2langKey(path, '$' + e + '.textStyle.' + String(i)), {
          returnObjects: true,
          defaultValue: {},
          fallbackLng: 'de',
        });
        result.push(new TextStyle({...def, ...cfg}));
      }
      return result;
    },
    [items, path, t],
  );

  const textAnchors = useMemo(() => getAnchors('cards'), [getAnchors]);
  const textStyles = useMemo(() => getStyles('cards'), [getStyles]);
  const answerAnchors = useMemo(() => getAnchors('answers'), [getAnchors]);
  const answerStyles = useMemo(() => getStyles('answers'), [getStyles]);

  const [answerIndex, setAnswerIndex] = useState<number | null>(null);

  const [selectedCard, _setSelectedCard] = useState<number | null>(null);
  const animIdRef = useRef(0);

  const setSelectedCard = useCallback(
    (card: number | null) => {
      const yShow = {y: stageSize[1]};
      const yHide = {y: yShow.y + CardSize[1] * answerCardScale + 100};

      const animId = animIdRef.current + 1;
      animIdRef.current = animId;

      if (card !== null && selectedCard === null) {
        anim.start({to: yShow, delay: 250});
        setAnswerIndex(card);
      } else if (card === null && selectedCard !== null) {
        anim.start({
          to: yHide,
          delay: 250,
          onRest: () => {
            if (animIdRef.current === animId) setAnswerIndex(null);
          },
        });
      } else if (card !== null && selectedCard !== null) {
        anim.start({
          to: yHide,
          onRest: () => {
            if (animIdRef.current === animId) {
              anim.start({to: yShow});
              setAnswerIndex(card);
            }
          },
        });
      }

      _setSelectedCard(card);
    },
    [anim, answerCardScale, selectedCard, stageSize],
  );

  const answerImage = answerIndex !== null ? answerImages[answerIndex] : undefined;

  const stage =
    stageSize[0] > 1 && stageSize[1] > 1 ? (
      <Stage
        width={stageSize[0]}
        height={stageSize[1]}
        raf={false}
        renderOnComponentChange={true}
        options={{resizeTo: window, resolution: devicePixelRatio, backgroundAlpha: 0}}>
        <Container sortableChildren={true}>
          {cardImages.map((image, index) => (
            <Card
              key={index}
              index={index}
              stageSize={stageSize}
              image={image}
              text={texts[index]?.question ?? ''}
              textAnchor={textAnchors[index]}
              textStyle={textStyles[index]}
              selected={index === selectedCard}
              onDragStart={() => setSelectedCard(null)}
              onClick={() => setSelectedCard(index === selectedCard ? null : index)}
            />
          ))}
        </Container>
        {hasAnswers && (
          <AnimatedContainer
            x={0.5 * stageSize[0]}
            {...motion}
            scale={answerCardScale}
            eventMode="passive">
            <Sprite
              image={'assets/card_shadow.png'}
              y={45}
              anchor={[0.5, 1]}
              width={CardSize[0] + 90}
              height={CardSize[1] + 90}
              eventMode="none"
            />
            <Sprite
              anchor={[0.5, 1]}
              width={CardSize[0]}
              height={CardSize[1]}
              texture={Texture.WHITE}
              alpha={0.9}
              eventMode="static"
              cursor="pointer"
              onclick={() => setSelectedCard(null)}
            />
            <Sprite
              anchor={[0.5, 1]}
              width={CardSize[0]}
              height={CardSize[1]}
              texture={typeof answerImage === 'string' ? undefined : Texture.WHITE}
              image={typeof answerImage === 'string' ? answerImage : undefined}
              tint={typeof answerImage === 'number' ? answerImage : 0xffffff}
              eventMode="none"
            />
            {answerIndex !== null && (
              <Text
                text={texts[answerIndex]?.answer ?? ''}
                anchor={answerAnchors[answerIndex]}
                y={-0.5 * CardSize[1]}
                style={answerStyles[answerIndex]}
                eventMode="none"
              />
            )}
          </AnimatedContainer>
        )}
      </Stage>
    ) : (
      <></>
    );

  return (
    <FullScreenView image={image} imagePosition={imagePosition}>
      {stage}
    </FullScreenView>
  );
};
export default TableScreen;

const AnimatedContainer = animated(Container);

const getAnswerCardScale = (stageSize: [number, number]) => {
  const w1 = 0.8 * stageSize[0];
  const h1 = (w1 * CardSize[1]) / CardSize[0];
  const h2 = 0.4 * stageSize[1];
  const w2 = (h2 * CardSize[0]) / CardSize[1];

  const minAreaW = h1 * w1 < h2 * w2 ? w1 : w2;
  return minAreaW / CardSize[0];
};

const getImages = (
  t: TFunction,
  path: string,
  e: 'cards' | 'answers',
  items: number,
): Array<string | number | undefined> => {
  const assets = configuration.assetsPath(path);
  const cfg: any = t(configuration.path2langKey(path, '$' + e + '.images'), {
    returnObjects: true,
    defaultValue: false,
    fallbackLng: 'de',
  });

  if (cfg === false) return Array(items).fill(undefined);
  if (cfg && Array.isArray(cfg))
    return cfg.map(f => (typeof f === 'number' ? f : assets + String(f)));
  if (typeof cfg === 'string') return Array(items).fill(assets + cfg);
  if (typeof cfg === 'number') return Array(items).fill(cfg);

  const baseName = assets + (e === 'cards' ? 'card' : 'answer');
  const result: string[] = [];
  for (let i = 1; i <= items; i++) {
    result.push(baseName + String(i).padStart(2, '0') + '.jpg');
  }
  return result;
};

const getDefault = <T extends object>(
  t: TFunction,
  path: string,
  defaultKey: string,
  defaultValue: T,
  merge: boolean,
): T => {
  const cfg: Partial<T> = t(
    [
      configuration.path2langKey(path, defaultKey),
      configuration.path2langKey(path, defaultKey.replace('.default', '')),
    ],
    {
      returnObjects: true,
      defaultValue: merge ? {} : defaultValue,
      fallbackLng: 'de',
    },
  );
  return merge ? {...defaultValue, ...cfg} : (cfg as T);
};

const DefaultCardTextStyle: Partial<ITextStyle> = {
  align: 'center',
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
  fontSize: 50,
  fontWeight: 'bold',
  fill: '#404040',
  wordWrap: true,
  wordWrapWidth: 700,
};

const DefaultAnswerTextStyle: Partial<ITextStyle> = {
  ...DefaultCardTextStyle,
  align: 'left',
};
